import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConfidenceBadge } from "@/components/Badges";
import { GitCompareArrows, TriangleAlert } from "lucide-react";

const PAIR_LABEL = { "IT-DP": "PAN ↔ Darpan", "IT-FC": "PAN ↔ FCRA", "DP-FC": "Darpan ↔ FCRA" };

export default function Candidates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // pull review-tier links across all entities via mapping entries that have open reviews
    const load = async () => {
      try {
        const m = await api.get("/v1/mapping", { params: { page: 1, page_size: 100, tier: "review" } });
        const entities = m.data.rows.filter((r) => r.open_reviews > 0).slice(0, 25);
        const all = [];
        for (const e of entities) {
          const res = await api.get(`/v1/ngo/${encodeURIComponent(e.entity_key)}/candidates`);
          res.data.candidates.forEach((c) => all.push({ ...c, entity: e.display_name, entity_key: e.entity_key }));
        }
        setRows(all);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Possible, not verified"
        title="Unverified Candidate Links"
        subtitle="Plausible matches that a model proposed but no reviewer has confirmed. These are NEVER treated as linked identities."
      />

      <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 max-w-3xl">
        <TriangleAlert className="w-4 h-4 shrink-0" />
        Around 73% of review-tier links turn out to be true matches. Treat them as leads, not facts.
      </div>

      {loading ? (
        <div className="font-mono text-xs text-slate-500">Loading candidates…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-400">
          No pending candidate links.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((c, i) => (
            <div
              key={i}
              data-testid={`candidate-link-${i}`}
              className="rounded-xl border border-amber-200 bg-slate-900/60 p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <GitCompareArrows className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="font-medium text-slate-200 truncate">{c.entity}</span>
                  <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-[10px] text-slate-400">{PAIR_LABEL[c.pair_type]}</span>
                </div>
                <ConfidenceBadge info={c.tier_info} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-slate-400">
                <span>{c.left_id} ↔ {c.right_id}</span>
                {c.reason && <span className="text-amber-600">reason: {c.reason}</span>}
                {c.evidence?.name_similarity != null && <span>name sim: {c.evidence.name_similarity}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
